<template>
  <b-modal v-model="isActive" :full-screen="$mq == 'xs'" class="flow-modal" :can-cancel="false" :has-modal-card="true" scroll="keep">
    <div class="modal-card intro-tips" width="100px">
      <header class="modal-card-head">
        <div class="logo-container">
          <img :src="`${clientResourcesUrl}logo.png?alt=media`" alt="App Logo" class="app-logo">
        </div>
        <p :class="['modal-card-title','has-text-center', $mq]">{{ title }}</p>
      </header>            
      <component 
        :is="currentComponent"
        @set-title="title = $event"
        @width-change="setModalWidth"
        @reset="reset"
        @set-prev-route="setPrevRoute"
        @set-next-route="setNextRoute"
        @set-next-button-label="setNextButtonLabel"
        @set-next-button-enabled="setNextButtonEnabled"
        @set-next-button-icon="setNextButtonIcon"
        @set-prev-button-label="setPrevButtonLabel"
        @set-prev-button-enabled="setPrevButtonEnabled"
        @set-prev-button-icon="setPrevButtonIcon"
        @set-show-next="setShowNext"
        @set-show-prev="setShowPrev"
        @set-show-controls="setShowControls"
        :next-button-clicked="nextButtonClicked"
        :prev-button-clicked="prevButtonClicked"
      ></component>
      <footer class="modal-card-footer">    
        <LeadWorkflowNavigationButtons      
          v-if="showControls" 
          :next-button-label="nextButtonLabel"
          :next-button-enabled="nextButtonEnabled"
          :next-button-icon="nextButtonIcon"
          :prev-button-label="prevButtonLabel"
          :prev-button-enabled="prevButtonEnabled"
          :prev-button-icon="prevButtonIcon"
          :show-next="showNext"
          :show-prev="showPrev"
          :next-route="nextRoute"
          :prev-route="prevRoute"
          @next-button-clicked="handleNextButtonClick"
          @prev-button-clicked="handlePrevButtonClick"
          @next-button-mouseup="nextButtonClicked = false"
          @prev-button-mouseup="prevButtonClicked = false"
        ></LeadWorkflowNavigationButtons>
      </footer>      
    </div>        
  </b-modal>    
</template>

<style>
  .flow-modal .modal-card-head {
    /* display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    align-content: center;
    flex-wrap: wrap;
    flex-basis: 50%; */
    position: relative;
  }

  .flow-modal .app-logo {
    width: 100px;
    
    position: absolute;
    top: 0;
    left: 0;
    padding: 20px 0 0 20px;
  }

  .app-name {
    font-size: 1.2em;
  }

  .flow-modal .modal-card-title {
    text-align: center;
    font-size: 1.3em;
    font-weight: bold;
  }

  .flow-modal .modal-card-title.sm {
    padding-left: 3.8em;
  }

  .flow-modal p.modal-card-title.xs {
    font-size: 1.2em;
    margin-left: 70px;
  }
</style>

<script>
import { ref, computed, watch, onMounted, markRaw } from 'vue'
import { useRoute } from 'vue-router'
import { useMainStore } from '@/stores/main'
import { useConfig } from '@/composables/useConfig'
import Introduction from './lead_workflow_components/Introduction.vue'
import Tutorial from './lead_workflow_components/Tutorial.vue'
import AddressSearch from './lead_workflow_components/AddressSearch.vue'
import GetMeasurements from './lead_workflow_components/GetMeasurements.vue'
import Results from './lead_workflow_components/Results.vue'
import LeadWorkflowNavigationButtons from './lead_workflow_components/LeadWorkflowNavigationButtons.vue'

export default {
  name: 'FlowModal',
  components: {
    LeadWorkflowNavigationButtons
  },
  props: {
    firstStepName: {
      type: String
    }
  },
  setup(props) {
    const route = useRoute()
    const mainStore = useMainStore()
    const { clientResourcesUrl } = useConfig()

    const isActive = ref(true)
    const title = ref('')
    const maxWidth = ref(200)
    const components = markRaw({
      'introduction': Introduction,
      'address-search': AddressSearch,
      'tutorial': Tutorial,
      'get-measurements': GetMeasurements,
      'results': Results
    })
    const prevRoute = ref('')
    const nextRoute = ref('')
    const nextButtonLabel = ref('Next Step')
    const nextButtonEnabled = ref(true)
    const nextButtonIcon = ref('')
    const prevButtonLabel = ref('Previous Step')
    const prevButtonEnabled = ref(true)
    const prevButtonIcon = ref('')
    const showNext = ref(false)
    const showPrev = ref(false)
    const nextButtonClicked = ref(false)
    const prevButtonClicked = ref(false)
    const showControls = ref(true)

    // Computed
    const currentComponent = computed(() => {
      const stepName = route.params.stepName
      console.log('FlowModal: stepName =', stepName, 'firstStepName =', props.firstStepName)
      console.log('FlowModal: components =', components)
      const component = components && (components[stepName] || components[props.firstStepName])
      console.log('FlowModal: currentComponent =', component)
      return component
    })

    // Methods
    const setModalWidth = (width) => {
      maxWidth.value = width
    }

    const reset = () => {
      console.log('resetting state')
      mainStore.resetState()
    }

    const handleNextButtonClick = () => {
      nextButtonClicked.value = true
    }

    const handlePrevButtonClick = () => {
      prevButtonClicked.value = true
    }

    const setShowControls = (show) => {
      showControls.value = show
    }

    const setPrevRoute = (routePath) => {
      prevRoute.value = routePath
    }

    const setNextRoute = (routePath) => {
      nextRoute.value = routePath
    }

    const setNextButtonLabel = (label) => {
      console.log('setting next button label', label)
      nextButtonLabel.value = label
    }

    const setNextButtonEnabled = (enabled) => {
      nextButtonEnabled.value = enabled
    }

    const setNextButtonIcon = (icon) => {
      nextButtonIcon.value = icon
    }

    const setPrevButtonLabel = (label) => {
      prevButtonLabel.value = label
    }

    const setPrevButtonEnabled = (enabled) => {
      prevButtonEnabled.value = enabled
    }

    const setPrevButtonIcon = (icon) => {
      prevButtonIcon.value = icon
    }

    const setShowNext = (show) => {
      showNext.value = show
    }

    const setShowPrev = (show) => {
      showPrev.value = show
    }

    // Watchers
    watch(currentComponent, (val) => {
      console.log('FlowModal: currentComponent changed to', val)
    }, { immediate: true })

    // Lifecycle
    onMounted(() => {
      console.log('FlowModal mounted, route params:', route.params)
      console.log('FlowModal mounted, firstStepName prop:', props.firstStepName)
    })

    return {
      isActive,
      title,
      maxWidth,
      prevRoute,
      nextRoute,
      nextButtonLabel,
      nextButtonEnabled,
      nextButtonIcon,
      prevButtonLabel,
      prevButtonEnabled,
      prevButtonIcon,
      showNext,
      showPrev,
      nextButtonClicked,
      prevButtonClicked,
      showControls,
      currentComponent,
      clientResourcesUrl,
      setModalWidth,
      reset,
      handleNextButtonClick,
      handlePrevButtonClick,
      setShowControls,
      setPrevRoute,
      setNextRoute,
      setNextButtonLabel,
      setNextButtonEnabled,
      setNextButtonIcon,
      setPrevButtonLabel,
      setPrevButtonEnabled,
      setPrevButtonIcon,
      setShowNext,
      setShowPrev
    }
  }
}
</script>